// services/cache/CacheManager.js

/**
 * CacheManager - Centralized cache management
 * 
 * Phase 2 implementation that wraps existing cache operations
 * without changing structure. This allows us to gradually
 * migrate all cache access through a single point.
 * 
 * IMPORTANT: This hasn't been described well yet, it was kinda last minute. IT WORKS!
 */

// Gatsby's static-HTML pass runs this module in Node, where sessionStorage /
// localStorage don't exist. Every read used to throw, get caught, and
// console.error — flooding build logs with hundreds of scary-but-harmless
// stack traces (and burying real SSR errors). Short-circuit to "no cache"
// during SSR instead; the browser rehydrates normally on the client.
const isBrowser = typeof window !== 'undefined';

class CacheManager {
  constructor() {
    // Track cache operations for debugging
    this.operations = [];
    this.maxOperationHistory = 50;
  }

  /**
   * Log operations in development
   * Note: Logging disabled to reduce console noise (tracked internally only)
   */
  logOperation(type, key, success = true) {
    if (process.env.NODE_ENV === 'development') {
      const op = {
        type,
        key,
        success,
        timestamp: Date.now()
      };
      this.operations.push(op);
      if (this.operations.length > this.maxOperationHistory) {
        this.operations.shift();
      }
      // Silenced - use cacheManager.operations to inspect if needed
    }
  }

  // ===== AUTH STATE MANAGEMENT =====

  /**
   * Save auth-related state (preserves existing structure)
   */
  saveAuthState(state) {
    try {
      // Save to sessionStorage (existing keys)
      if (state.userProfile) {
        sessionStorage.setItem('userProfileCache', JSON.stringify(state.userProfile));
      }

      if (state.token && state.tokenExpiry) {
        const tokenState = {
          token: state.token,
          refreshToken: state.refreshToken,
          idToken: state.idToken,
          expiresAt: state.tokenExpiry
        };
        sessionStorage.setItem('keycloakTokenState', JSON.stringify(tokenState));
      }

      // Simplified auth info for quick checks
      if (state.authenticated !== undefined) {
        const authInfo = {
          authenticated: state.authenticated,
          projectId: state.projectId || state.userProfile?.projectId,
          email: state.userProfile?.email || '',
          timestamp: new Date().toISOString()
        };
        sessionStorage.setItem('authInfo', JSON.stringify(authInfo));
      }

      this.logOperation('saveAuthState', 'auth-bundle');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error saving auth state:', error);
      this.logOperation('saveAuthState', 'auth-bundle', false);
      return false;
    }
  }

  /**
   * Get auth state from cache (checks all sources)
   */
  getAuthState() {
    if (!isBrowser) return null;
    try {
      // Check sessionStorage first
      const userProfileJson = sessionStorage.getItem('userProfileCache');
      const tokenStateJson = sessionStorage.getItem('keycloakTokenState');
      const authInfoJson = sessionStorage.getItem('authInfo');

      let result = {
        authenticated: false,
        userProfile: null,
        token: null,
        tokenExpiry: null,
        projectId: null
      };

      // Parse user profile
      if (userProfileJson) {
        try {
          result.userProfile = JSON.parse(userProfileJson);
          result.projectId = result.userProfile.projectId;
        } catch (e) {
          console.warn('[CacheManager] Invalid userProfileCache');
        }
      }

      // Parse token state
      if (tokenStateJson) {
        try {
          const tokenState = JSON.parse(tokenStateJson);
          result.token = tokenState.token;
          result.refreshToken = tokenState.refreshToken;
          result.idToken = tokenState.idToken;
          result.tokenExpiry = tokenState.expiresAt;
        } catch (e) {
          console.warn('[CacheManager] Invalid keycloakTokenState');
        }
      }

      // Parse auth info as fallback
      if (authInfoJson && !result.projectId) {
        try {
          const authInfo = JSON.parse(authInfoJson);
          result.authenticated = authInfo.authenticated;
          result.projectId = result.projectId || authInfo.projectId;
        } catch (e) {
          console.warn('[CacheManager] Invalid authInfo');
        }
      }

      // Validate token expiry
      if (result.token && result.tokenExpiry) {
        const now = Date.now();
        if (now >= result.tokenExpiry) {
          this.clearAuthState();
          return null;
        }
        result.authenticated = true;
      }

      this.logOperation('getAuthState', 'auth-bundle');
      return result.authenticated ? result : null;
    } catch (error) {
      console.error('[CacheManager] Error getting auth state:', error);
      this.logOperation('getAuthState', 'auth-bundle', false);
      return null;
    }
  }

  /**
   * Clear auth-related cache
   */
  clearAuthState() {
    try {
      const authKeys = [
        'userProfileCache',
        'keycloakTokenState',
        'authInfo',
        'loginAttempted',
        'loginAttemptTimestamp',
        'redirectAfterAuth',
        'redirectedToKeycloak',
        'redirectedToKeycloakTimestamp'
      ];

      authKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Continue clearing other keys
        }
      });

      this.logOperation('clearAuthState', 'auth-bundle');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error clearing auth state:', error);
      this.logOperation('clearAuthState', 'auth-bundle', false);
      return false;
    }
  }

  // ===== CROSS-TAB SYNC =====

  /**
   * Save cross-tab auth info
   */
  saveCrossTabAuth(authData) {
    try {
      if (!authData.projectId || !authData.token) {
        console.warn('[CacheManager] Missing required data for cross-tab auth');
        return false;
      }

      const crossTabAuth = {
        authenticated: true,
        projectId: authData.projectId,
        user: authData.userProfile || { projectId: authData.projectId },
        token: authData.token,
        // Also store refresh and id tokens for logout to work after new tab
        refreshToken: authData.refreshToken || null,
        idToken: authData.idToken || null,
        timestamp: Date.now()
      };

      localStorage.setItem('crossTabAuthInfo', JSON.stringify(crossTabAuth));
      this.logOperation('saveCrossTabAuth', 'crossTabAuthInfo');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error saving cross-tab auth:', error);
      this.logOperation('saveCrossTabAuth', 'crossTabAuthInfo', false);
      return false;
    }
  }

  /**
   * Get cross-tab auth info (with validation)
   */
  getCrossTabAuth() {
    if (!isBrowser) return null;
    try {
      const crossTabJson = localStorage.getItem('crossTabAuthInfo');
      if (!crossTabJson) return null;

      const crossTabAuth = JSON.parse(crossTabJson);

      // Check freshness (30 minutes max)
      const MAX_AGE = 30 * 60 * 1000;
      if (Date.now() - crossTabAuth.timestamp > MAX_AGE) {
        localStorage.removeItem('crossTabAuthInfo');
        return null;
      }

      this.logOperation('getCrossTabAuth', 'crossTabAuthInfo');
      return crossTabAuth;
    } catch (error) {
      console.error('[CacheManager] Error getting cross-tab auth:', error);
      this.logOperation('getCrossTabAuth', 'crossTabAuthInfo', false);
      return null;
    }
  }

  /**
   * Clear cross-tab data
   */
  clearCrossTabData() {
    try {
      const crossTabKeys = [
        'crossTabAuthInfo',
        'logout_event',
        'lastUserActivity'
      ];

      crossTabKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Continue clearing other keys
        }
      });

      // Clear rebrand-cleanup gates' localStorage as part of cross-tab cleanup
      this.clearRebrandGateData();

      this.logOperation('clearCrossTabData', 'cross-tab-bundle');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error clearing cross-tab data:', error);
      this.logOperation('clearCrossTabData', 'cross-tab-bundle', false);
      return false;
    }
  }

  // ===== REBRAND-CLEANUP GATE MANAGEMENT =====

  /**
   * Clear localStorage / sessionStorage entries used by the
   * TermsAcceptanceGate + AnnouncementHost components. Critical for the
   * shared-device case: user A logs out → user B logs in → B must hit the
   * gate fresh and see announcements fresh, not inherit A's cache.
   *
   * Also clears any stragglers from the legacy relaunch dialog
   * (relaunchSeen_* / relaunchCheckTimestamp / relaunchViewedEvent) so a
   * mid-deploy upgrade doesn't leave dangling keys behind.
   */
  clearRebrandGateData() {
    try {
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith('termsAccepted_') ||
            key.startsWith('announcement_state_') ||
            key === 'announcement_check_at' ||
            // Legacy stragglers
            key.startsWith('relaunchSeen_') ||
            key === 'relaunchCheckTimestamp')
        ) {
          keysToRemove.push(key);
        }
      }

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          console.warn(`[CacheManager] Error removing gate key ${key}:`, e);
        }
      });

      try {
        sessionStorage.removeItem('relaunchViewedEvent');
      } catch (e) {
        // Ignore
      }

      // Sweep session-scoped Terms gate "Later" flags so user B logging
      // in after user A on the same tab doesn't inherit A's deferral.
      try {
        const sessionKeysToRemove = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('termsLaterDeferred_')) {
            sessionKeysToRemove.push(key);
          }
        }
        sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
      } catch (e) {
        // Ignore
      }

      this.logOperation('clearRebrandGateData', 'rebrand-gate-bundle');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error clearing rebrand gate data:', error);
      this.logOperation('clearRebrandGateData', 'rebrand-gate-bundle', false);
      return false;
    }
  }

  // ===== APP STATE MANAGEMENT =====

  /**
   * Save app state (preserves existing structure)
   */
  saveAppState(state) {
    try {
      const timestamp = new Date().toISOString();
      const projectId = state.projectId || this.getProjectId();

      const stateToSave = {
        timestamp,
        initialized: state.initialized,
        projectId,
        cacheVersion: '2.0', // Existing version
        data: state.data || {},
        sessionInfo: state.sessionInfo || {},
        authInfo: state.authInfo || {},
        apiHealth: state.apiHealth || {}
      };

      sessionStorage.setItem('appState', JSON.stringify(stateToSave));
      sessionStorage.setItem('appSessionActive', 'true');
      sessionStorage.setItem('appLastInitTimestamp', timestamp);

      this.logOperation('saveAppState', 'app-bundle');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error saving app state:', error);
      
      // Try minimal save if full save fails
      try {
        const minimalState = {
          timestamp: new Date().toISOString(),
          initialized: state.initialized,
          projectId: state.projectId
        };
        sessionStorage.setItem('appState', JSON.stringify(minimalState));
        return true;
      } catch (e) {
        this.logOperation('saveAppState', 'app-bundle', false);
        return false;
      }
    }
  }

  /**
   * Get app state from cache
   */
  getAppState() {
    if (!isBrowser) return null;
    try {
      const appStateJson = sessionStorage.getItem('appState');
      if (!appStateJson) return null;

      const appState = JSON.parse(appStateJson);

      // Check freshness (30 minutes)
      const timestamp = new Date(appState.timestamp || 0);
      const isFresh = (new Date() - timestamp) < (30 * 60 * 1000);

      if (!isFresh) {
        return null;
      }

      this.logOperation('getAppState', 'app-bundle');
      return appState;
    } catch (error) {
      console.error('[CacheManager] Error getting app state:', error);
      this.logOperation('getAppState', 'app-bundle', false);
      return null;
    }
  }

  /**
   * Clear app state cache
   */
  clearAppState() {
    try {
      const appKeys = [
        'appState',
        'appSessionActive',
        'appLastInitTimestamp',
        // Module-specific caches
        'appCache_customerRequests',
        'appCache_businessSegments',
        'appCache_valueLists'
      ];

      appKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Continue clearing other keys
        }
      });

      this.logOperation('clearAppState', 'app-bundle');
      return true;
    } catch (error) {
      console.error('[CacheManager] Error clearing app state:', error);
      this.logOperation('clearAppState', 'app-bundle', false);
      return false;
    }
  }

  // ===== UTILITY METHODS =====

  /**
   * Get project ID from any available source
   */
  getProjectId() {
    if (!isBrowser) return null;
    // Check auth state first
    const authState = this.getAuthState();
    if (authState?.projectId) {
      return authState.projectId;
    }

    // Check app state
    const appState = this.getAppState();
    if (appState?.projectId) {
      return appState.projectId;
    }

    // Check cross-tab
    const crossTab = this.getCrossTabAuth();
    if (crossTab?.projectId) {
      return crossTab.projectId;
    }

    return null;
  }

  /**
   * Check if we have valid cached data for fast-path init
   */
  canUseFastPath() {
    const authState = this.getAuthState();
    const appState = this.getAppState();

    if (!authState || !appState) {
      return false;
    }

    // Must have valid token
    if (!authState.token || !authState.tokenExpiry) {
      return false;
    }

    // Must have matching project IDs
    if (authState.projectId !== appState.projectId) {
      return false;
    }

    // Must have initialized app state
    if (!appState.initialized) {
      return false;
    }

    return true;
  }

  /**
   * Clear ALL cache (complete reset)
   * Includes rebrand-cleanup gate data for proper multi-user support.
   */
  clearAll() {
    // Clear sessionStorage
    this.clearAuthState();
    this.clearAppState();

    // Clear localStorage
    this.clearCrossTabData();

    // Belt-and-braces: ensure rebrand gate keys are cleared in case the
    // cross-tab pass missed them.
    this.clearRebrandGateData();

    // Clear other session items
    try {
      const otherKeys = [
        'controlledLogout',
        'skipAuthProcessing',
        'onLogoutPage',
        'invitationAccepted',
        'recentInvitationAcceptance',
        'scmPermissions',
        'projectPrivileges'
      ];

      otherKeys.forEach(key => {
        try {
          sessionStorage.removeItem(key);
        } catch (e) {
          // Continue
        }
      });
    } catch (e) {
      // Continue
    }

    // Clear first-run / guided mode state (prevents GUIDED_MODE bleeding between accounts)
    try {
      ['firstRunComplete', 'firstRunWelcomeShown', 'firstRunProgress',
       'firstRunCompleteTimestamp', 'showFirstRunWelcome'
      ].forEach(key => {
        try { localStorage.removeItem(key); } catch (e) { /* Continue */ }
      });
    } catch (e) {
      // Continue
    }

    // Clear Keycloak-specific localStorage items
    try {
      const keycloakKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('kc-') || key.includes('keycloak'))) {
          keycloakKeys.push(key);
        }
      }

      keycloakKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (e) {
          // Continue
        }
      });
    } catch (e) {
      // Continue
    }

    this.logOperation('clearAll', 'all-cache');
    return true;
  }

  /**
   * Get cache statistics (for debugging)
   */
  getCacheStats() {
    const stats = {
      sessionStorage: {},
      localStorage: {},
      operations: this.operations.slice(-10) // Last 10 operations
    };

    // Count sessionStorage items
    try {
      stats.sessionStorage.totalItems = sessionStorage.length;
      stats.sessionStorage.totalSize = new Blob(
        Object.values(sessionStorage)
      ).size;
    } catch (e) {
      stats.sessionStorage.error = e.message;
    }

    // Count localStorage items
    try {
      stats.localStorage.totalItems = localStorage.length;
      stats.localStorage.totalSize = new Blob(
        Object.values(localStorage)
      ).size;
    } catch (e) {
      stats.localStorage.error = e.message;
    }

    return stats;
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();

// Also export class for testing
export { CacheManager };